import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  section: { padding: theme.spacing(2) },
  title: { fontWeight: 600, marginBottom: theme.spacing(1) },
  kpi: { fontSize: '1.2rem', fontWeight: 700 },
  muted: { color: '#666' },
  table: {
    '& .MuiTableCell-root': { padding: '6px 10px', fontSize: '0.85rem' },
    '& .MuiTableCell-head': { fontWeight: 600, backgroundColor: '#f5f5f5' },
  },
}));

const SystemHealth = () => {
  const classes = useStyles();
  const { token, user } = useContext(AuthContext);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const res = await fetch(`${API_URL}admin/config/health`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        setData(payload);
      } catch (e) {
        setError(e.message);
      }
    };
    fetchHealth();
  }, [API_URL, token]);

  if (error) {
    return (
      <Box className={classes.root}>
        <Typography variant="h6" color="error">Failed to load system health</Typography>
        <Typography variant="body2" className={classes.muted}>{String(error)}</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box className={classes.root}>
        <Typography variant="body2" className={classes.muted}>Loading…</Typography>
      </Box>
    );
  }

  const doSvc = data?.services?.digitalOcean || {};

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.title}>System Health</Typography>
      <Typography variant="caption" className={classes.muted}>As of {new Date(data.timestamp).toLocaleString()}</Typography>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>API & Environment</Typography>
            <Typography variant="body2">API: <b>{data?.services?.api ? 'up' : 'down'}</b></Typography>
            <Typography variant="body2">Database: <b>{data?.services?.database ? 'up' : 'down'}</b></Typography>
            <Typography variant="body2">CORS: <span className={classes.muted}>{Object.keys(data?.env?.cors || {}).filter(k => data.env.cors[k]).join(', ')}</span></Typography>
            <Typography variant="body2">Strict Mode: <b>{data?.env?.strictMode ? 'ON' : 'OFF'}</b></Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>App Scheduler</Typography>
            <Typography variant="body2">Enabled: <b>{data?.services?.scheduler?.enabled ? 'yes' : 'no'}</b></Typography>
            <Typography variant="body2">Last Run: <span className={classes.muted}>{data?.services?.scheduler?.lastRunAt ? new Date(data.services.scheduler.lastRunAt).toLocaleString() : '—'}</span></Typography>
            <Typography variant="body2">Processing: <b>{data?.services?.scheduler?.processing ? 'yes' : 'no'}</b></Typography>
            {data?.services?.scheduler?.lastResult && (
              <Typography variant="caption" className={classes.muted}>Result: {JSON.stringify(data.services.scheduler.lastResult)}</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>DigitalOcean</Typography>
            <Typography variant="body2">Configured: <b>{doSvc?.enabled ? 'yes' : 'no'}</b></Typography>
            <Typography variant="body2">Live Route: <b>{doSvc?.liveRouteEnabled ? 'enabled' : 'disabled'}</b></Typography>
            <Typography variant="body2">Nightly: <b>{doSvc?.nightly?.enabled ? `ON @ ${doSvc?.nightly?.hourUTC}:00 UTC` : 'OFF'}</b></Typography>
            <Typography variant="body2">Next Run: <span className={classes.muted}>{doSvc?.nightly?.nextRun ? new Date(doSvc.nightly.nextRun).toLocaleString() : '—'}</span></Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>Last DO Ingest</Typography>
            {doSvc?.lastIngest ? (
              <>
                <Typography variant="body2">Mode: <b>{doSvc.lastIngest.mode}</b></Typography>
                <Typography variant="body2">Started: <span className={classes.muted}>{new Date(doSvc.lastIngest.started_at).toLocaleString()}</span></Typography>
                <Typography variant="body2">Finished: <span className={classes.muted}>{doSvc.lastIngest.finished_at ? new Date(doSvc.lastIngest.finished_at).toLocaleString() : '—'}</span></Typography>
                <Typography variant="body2">Projects: <b>{doSvc.lastIngest.projects_processed}</b> • Resources: <b>{doSvc.lastIngest.resources_processed}</b> • API Calls: <b>{doSvc.lastIngest.api_calls}</b></Typography>
              </>
            ) : (
              <Typography variant="body2" className={classes.muted}>No ingest logs.</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>Last DO Sync</Typography>
            {doSvc?.lastSync ? (
              <>
                <Typography variant="body2">Completed: <span className={classes.muted}>{new Date(doSvc.lastSync.completed_at).toLocaleString()}</span></Typography>
                <Typography variant="body2">Processed: <b>{doSvc.lastSync.resources_processed}</b> • Created: <b>{doSvc.lastSync.assets_created}</b> • Updated: <b>{doSvc.lastSync.assets_updated}</b> • Deactivated: <b>{doSvc.lastSync.assets_deactivated}</b></Typography>
              </>
            ) : (
              <Typography variant="body2" className={classes.muted}>No sync logs.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>Recent DO Ingest (5)</Typography>
            <TableContainer>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Finished</TableCell>
                    <TableCell align="right">Proj</TableCell>
                    <TableCell align="right">Res</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(doSvc?.recent?.ingest || []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.mode}</TableCell>
                      <TableCell>{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</TableCell>
                      <TableCell>{r.finished_at ? new Date(r.finished_at).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">{r.projects_processed}</TableCell>
                      <TableCell align="right">{r.resources_processed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.section} variant="outlined">
            <Typography variant="subtitle1" className={classes.title}>Recent DO Sync (5)</Typography>
            <TableContainer>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell align="right">Res</TableCell>
                    <TableCell align="right">Created</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell align="right">Deactivated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(doSvc?.recent?.sync || []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">{r.resources_processed}</TableCell>
                      <TableCell align="right">{r.assets_created}</TableCell>
                      <TableCell align="right">{r.assets_updated}</TableCell>
                      <TableCell align="right">{r.assets_deactivated}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemHealth;
